import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { QUERY_KEYS, STALE_TIME } from '@/libs/constants'
import type { HookParams, Service, ServiceListParams, ServiceParams } from '@/libs/interfaces'
import { servicesApi } from '@/services/api'

export function useInfiniteServices({ params, enabled }: HookParams<ServiceListParams>) {
  return useInfiniteQuery({
    queryKey: QUERY_KEYS.services.infinite(params),
    queryFn: ({ pageParam }) =>
      servicesApi.listCursor({
        ...params,
        cursor: pageParam,
      }),
    getNextPageParam: (lastPage) => lastPage.pageInfo.next || undefined,
    getPreviousPageParam: (firstPage) => firstPage.pageInfo.prev || undefined,
    enabled: enabled ?? true,
    initialPageParam: undefined as string | undefined,
    staleTime: STALE_TIME['24Hours'], //24 Hours
  })
}

export function useServices({ params, enabled }: HookParams<ServiceListParams>) {
  return useQuery<Partial<Service[]>>({
    queryKey: QUERY_KEYS.services.list(params),
    queryFn: () => servicesApi.list(params),
    enabled: enabled ?? true,
    staleTime: STALE_TIME['24Hours'], //24 hours
  })
}

export function useCreateService() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: servicesApi.create,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.services.all,
      })
    },
    onError: () => {
      toast.error(t('common.error'))
    },
  })
}

export function useServicesByID({ params, enabled }: HookParams<ServiceParams>) {
  return useQuery<Partial<Service>>({
    queryKey: [...QUERY_KEYS.services.id(params.serviceID), params.orgID] as const,
    queryFn: () => servicesApi.getByID(params),
    enabled: (enabled ?? true) && !!params.serviceID && !!params.orgID,
    staleTime: STALE_TIME['24Hours'], //24 hours
  })
}

export function useUpdateServices() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: servicesApi.update,
    onSuccess: (_data) => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.services.all,
      })
    },
    onError: () => {
      toast.error(t('common.error'))
    },
  })
}

export function useServicesDelete() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: servicesApi.delete,
    onSuccess: (_data) => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.services.all,
      })
    },
    onError: (error) => {
      return error.message
    },
  })
}

export function useServicesVariantCreate() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: servicesApi.createVariant,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.services.all,
      })
    },
    onError: () => {
      toast.error(t('common.error'))
    },
  })
}

export function useServicesVariantUpdate() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: servicesApi.updateVariant,
    onSuccess: (_data) => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.services.all,
      })
    },
    onError: () => {
      toast.error(t('common.error'))
    },
  })
}

export function useServicesVariantDelete() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: servicesApi.deleteVariant,
    onSuccess: (_data) => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.services.all,
      })
    },
    onError: () => {
      toast.error(t('common.error'))
    },
  })
}
